/* ============================================================
   FLOWERTRAIL — Motion engine (shared by all ft-* sections)
   One IntersectionObserver for reveals, one rAF loop for tilt.
   Rules: no scroll-jacking, passive listeners only, everything
   gated behind prefers-reduced-motion, zero duplicate handlers.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer =
    window.matchMedia && window.matchMedia('(pointer: fine)').matches;

  /* ---------------- Reveal on scroll ---------------- */
  var revealObserver = null;

  function initReveals(root) {
    var els = (root || document).querySelectorAll('.ft-reveal:not(.is-in)');
    if (!els.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target); // reveal once, then forget
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    els.forEach(function (el) { revealObserver.observe(el); });
  }

  /* ---------------- Pointer tilt (3D card feel) ----------------
     Element opts in with data-ft-tilt. Max tilt is deliberately
     small (6deg) — depth, not a funfair. Desktop pointers only. */
  function initTilt(root) {
    if (reduceMotion || !finePointer) return;

    (root || document).querySelectorAll('[data-ft-tilt]:not([data-ft-tilt-on])').forEach(function (card) {
      card.setAttribute('data-ft-tilt-on', '');
      var raf = null;
      var nextX = 0, nextY = 0;
      var max = parseFloat(card.getAttribute('data-ft-tilt')) || 6;

      function render() {
        raf = null;
        card.style.transform =
          'perspective(900px) rotateX(' + nextY + 'deg) rotateY(' + nextX + 'deg)';
      }

      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        if (!r.width || !r.height) return;
        nextX = ((e.clientX - r.left) / r.width - 0.5) * 2 * max;
        nextY = -((e.clientY - r.top) / r.height - 0.5) * 2 * max;
        if (!raf) raf = requestAnimationFrame(render);
      }, { passive: true });

      card.addEventListener('pointerleave', function () {
        if (raf) { cancelAnimationFrame(raf); raf = null; }
        card.style.transform = '';
      }, { passive: true });
    });
  }

  /* ---------------- Saga progress rail ----------------
     Rail dots ([data-ft-rail-dot]) highlight the chapter currently
     in view and smooth-scroll to it on click. */
  function initRail() {
    var rails = document.querySelectorAll('[data-ft-rail]');
    rails.forEach(function (rail) {
      if (rail.hasAttribute('data-ft-rail-on')) return;
      rail.setAttribute('data-ft-rail-on', '');

      var dots = Array.prototype.slice.call(rail.querySelectorAll('[data-ft-rail-dot]'));
      var chapters = dots
        .map(function (d) { return document.getElementById(d.getAttribute('data-ft-rail-dot')); })
        .filter(Boolean);
      if (!chapters.length) return;

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          var target = chapters[i];
          if (!target) return;
          target.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start'
          });
        });
      });

      if (!('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var idx = chapters.indexOf(entry.target);
            if (idx === -1) return;
            if (entry.isIntersecting) {
              dots.forEach(function (d, i) {
                d.classList.toggle('is-active', i === idx);
                d.setAttribute('aria-current', i === idx ? 'true' : 'false');
              });
            }
          });
        },
        { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
      );
      chapters.forEach(function (ch) { io.observe(ch); });

      /* Rail is position:fixed — only show it while its own section
         is actually on screen, so it never floats over other content. */
      var sagaSection = rail.closest('.ft-saga');
      if (sagaSection) {
        var visIo = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              sagaSection.classList.toggle('is-rail-visible', entry.isIntersecting);
            });
          },
          { rootMargin: '-15% 0px -15% 0px', threshold: 0 }
        );
        visIo.observe(sagaSection);
      }
    });
  }

  /* ---------------- Atlas brand filter ----------------
     Buttons: [data-ft-filter="handle|all"], cards: [data-ft-brand].
     Pure show/hide with a CSS fade — never reorders the grid, so
     nothing jumps. */
  function initFilters() {
    document.querySelectorAll('[data-ft-filter-group]').forEach(function (group) {
      if (group.hasAttribute('data-ft-filter-on')) return;
      group.setAttribute('data-ft-filter-on', '');

      var scope = group.getAttribute('data-ft-filter-group');
      var gridRoot = scope ? document.getElementById(scope) : null;
      if (!gridRoot) return;
      var buttons = group.querySelectorAll('[data-ft-filter]');
      var cards = gridRoot.querySelectorAll('[data-ft-brand]');

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var want = btn.getAttribute('data-ft-filter');

          buttons.forEach(function (b) {
            var on = b === btn;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
          });

          cards.forEach(function (card) {
            var show = want === 'all' || card.getAttribute('data-ft-brand') === want;
            card.classList.toggle('ft-hidden', !show);
          });
        });
      });
    });
  }

  function start() {
    initReveals(document);
    initTilt(document);
    initRail();
    initFilters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /* Shopify theme editor: re-init when sections re-render */
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e.target);
    initTilt(e.target);
    initRail();
    initFilters();
  });

  window.FTMotion = { refresh: start };
})();
